export interface ImageViewerState {
  zoom: number;
  panX: number;
  panY: number;
  fitZoom: number;
  isFullscreen: boolean;
  controlsVisible: boolean;
  sidePanelOpen: boolean;
  sidePanelHasElement: boolean;
  sidePanelWidth: number;
  /** Whether the settings (quality) menu in the control bar is open. */
  sourceMenuOpen: boolean;
}

export type ImageViewerAction =
  | { type: 'ZOOM_IN' }
  | { type: 'ZOOM_OUT' }
  | { type: 'ZOOM_TO_FIT' }
  | { type: 'ZOOM_TO_100' }
  | { type: 'SET_ZOOM'; zoom: number; panX: number; panY: number }
  | { type: 'PAN'; panX: number; panY: number }
  | { type: 'ENTER_FULLSCREEN' }
  | { type: 'EXIT_FULLSCREEN' }
  | { type: 'SET_CONTROLS_VISIBLE'; visible: boolean }
  | { type: 'SET_FIT_ZOOM'; fitZoom: number; centerX: number; centerY: number; force?: boolean }
  | { type: 'TOGGLE_SIDE_PANEL' }
  | { type: 'SET_SIDE_PANEL'; open: boolean }
  | { type: 'SET_SIDE_PANEL_HAS_ELEMENT'; hasElement: boolean }
  | { type: 'SET_SIDE_PANEL_WIDTH'; width: number }
  | { type: 'TOGGLE_SOURCE_MENU' }
  | { type: 'CLOSE_SOURCE_MENU' };

const ZOOM_STEP = 1.25;
const MIN_ZOOM = 0.01; // absolute floor; fitZoom is the practical minimum
const MAX_ZOOM = 10;

function clampZoom(zoom: number, _fitZoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** Center the image in the viewport given its dimensions and zoom. */
function centerPan(zoom: number, viewportW: number, viewportH: number, imgW: number, imgH: number) {
  return {
    panX: (viewportW - imgW * zoom) / 2,
    panY: (viewportH - imgH * zoom) / 2,
  };
}

export function createInitialState(): ImageViewerState {
  return {
    zoom: 1,
    panX: 0,
    panY: 0,
    fitZoom: 1,
    isFullscreen: false,
    controlsVisible: true,
    sidePanelOpen: false,
    sidePanelHasElement: false,
    sidePanelWidth: 300,
    sourceMenuOpen: false,
  };
}

export function imageViewerReducer(state: ImageViewerState, action: ImageViewerAction): ImageViewerState {
  switch (action.type) {
    case 'ZOOM_IN': {
      const zoom = clampZoom(state.zoom * ZOOM_STEP, state.fitZoom);
      // Zoom toward center of viewport: keep viewport center fixed
      const scale = zoom / state.zoom;
      // We don't know viewport size here, so we scale pan around (0,0) relative to current transform.
      // The component will handle cursor-relative zoom via SET_ZOOM.
      const panX = state.panX * scale;
      const panY = state.panY * scale;
      return { ...state, zoom, panX, panY };
    }

    case 'ZOOM_OUT': {
      const zoom = clampZoom(state.zoom / ZOOM_STEP, state.fitZoom);
      const scale = zoom / state.zoom;
      const panX = state.panX * scale;
      const panY = state.panY * scale;
      return { ...state, zoom, panX, panY };
    }

    case 'ZOOM_TO_FIT': {
      return {
        ...state,
        zoom: state.fitZoom,
        panX: action.type === 'ZOOM_TO_FIT' ? state.panX : 0,
        panY: action.type === 'ZOOM_TO_FIT' ? state.panY : 0,
      };
      // Note: actual centering is done by SET_FIT_ZOOM which provides centerX/Y
    }

    case 'ZOOM_TO_100':
      return { ...state, zoom: clampZoom(1, state.fitZoom) };

    case 'SET_ZOOM':
      return {
        ...state,
        zoom: clampZoom(action.zoom, state.fitZoom),
        panX: action.panX,
        panY: action.panY,
      };

    case 'PAN':
      return { ...state, panX: action.panX, panY: action.panY };

    // Fullscreen transitions reflow the control bar the menu is anchored to,
    // so close it rather than leaving it floating at a stale position.
    case 'ENTER_FULLSCREEN':
      return { ...state, isFullscreen: true, sourceMenuOpen: false };

    case 'EXIT_FULLSCREEN':
      return { ...state, isFullscreen: false, sourceMenuOpen: false };

    case 'SET_CONTROLS_VISIBLE':
      return { ...state, controlsVisible: action.visible };

    case 'SET_FIT_ZOOM': {
      // If the user is still at the default/fit zoom, or this is an
      // explicit "zoom to fit" request (force), update zoom + pan to
      // keep the image centered.  Otherwise only update the fitZoom floor
      // so the user's manual zoom level is preserved (e.g. when the side
      // panel resizes the viewport).
      const wasAtFit = Math.abs(state.zoom - state.fitZoom) < 1e-6;
      if (wasAtFit || action.force) {
        return {
          ...state,
          fitZoom: action.fitZoom,
          zoom: action.fitZoom,
          panX: action.centerX,
          panY: action.centerY,
        };
      }
      // Clamp current zoom in case the new fitZoom is larger
      const zoom = Math.max(action.fitZoom, state.zoom);
      return { ...state, fitZoom: action.fitZoom, zoom };
    }

    case 'TOGGLE_SIDE_PANEL':
      return { ...state, sidePanelOpen: !state.sidePanelOpen };

    case 'SET_SIDE_PANEL':
      return { ...state, sidePanelOpen: action.open };

    case 'SET_SIDE_PANEL_HAS_ELEMENT':
      return { ...state, sidePanelHasElement: action.hasElement };

    case 'SET_SIDE_PANEL_WIDTH':
      return { ...state, sidePanelWidth: action.width };

    case 'TOGGLE_SOURCE_MENU':
      return { ...state, sourceMenuOpen: !state.sourceMenuOpen };

    case 'CLOSE_SOURCE_MENU':
      return { ...state, sourceMenuOpen: false };

    default:
      return state;
  }
}
